import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PageLayout from "../_layout/PageLayout";
import { CurrentJobStorageModel } from "../../services/job/CurrentJobStorage";
import {
  ViewSewerObjectsLinkedToJob,
  ViewSewerObjectsLinkedToAssignedJobOrJobProject,
} from "../../services/authorization/Permissions";
import SewerObjectsMap from "../sewer-objects/SewerObjectsMap";
import { api } from "../../services/api/ApiService";
import { SewerObjectsMapStorageModel } from "../../services/sewer-object/SewerObjectsMapStorage";
import useUrlParams from "../../services/url/UrlParamsService";
import { JobMenuItemsModel } from "./JobMenuItems";
import DataGrid from "../../components/datatable/DataGrid";
import { useHistory } from "react-router";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { Info } from "../../components/svg/svgImageList";
import Svg from "../../components/svg/Svg";
import { translationService } from "../../services/translation/TranslationService";
import SewerObjectsMapLegend from "../../components/map/SewerObjectsMapLegend";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const currentJobId = urlParamsService.getJobId()!;

  const [currentJobStorage] = useState(() =>
    CurrentJobStorageModel.create({ currentJobId: currentJobId }, { api })
  );

  const [mapStorage] = useState(() =>
    SewerObjectsMapStorageModel.create({ inputJobId: currentJobId, autoLoad: false }, { api })
  );

  const jobMenuItems = useMemo(
    () =>
      JobMenuItemsModel.create({
        jobId: currentJobId,
        isLoading: currentJobStorage.isLoading,
        numberOfSewerObjects: currentJobStorage.currentJob.sewerObjectsCount,
      }),
    [currentJobId, currentJobStorage.currentJob.sewerObjectsCount, currentJobStorage.isLoading]
  );

  const filterMapData = useCallback(async () => {
    if (mapStorage.sewerObjectsStorage.hasFilterApplied) {
      await mapStorage.setFilterData(mapStorage.sewerObjectsStorage.filterData.gridFilter);
    } else {
      await mapStorage.setFilterData(undefined);
    }
    mapStorage.setIsFilterMapDisabled(true);
  }, [mapStorage]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([
      {
        entity: EntityTypeFromPath.Job,
        name: currentJobStorage.currentJob.name,
      },
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentJobStorage.currentJob.name, history.location.pathname]);

  useEffect(() => {
    mapStorage.setDefaultSelectedItem(
      mapStorage.sewerObjectsStorage.selectedItemsCount === 1
        ? mapStorage.sewerObjectsStorage.selectedItems[0]
        : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStorage.sewerObjectsStorage.selectedItems[0], mapStorage]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[
        ViewSewerObjectsLinkedToJob,
        ViewSewerObjectsLinkedToAssignedJobOrJobProject,
      ]}
      titleKey="JobSewerObjectsMapPage.title"
      titleContext={{
        jobName: currentJobStorage.currentJob && currentJobStorage.currentJob.name,
      }}
      isLoading={currentJobStorage.isLoading}
      menuItems={{ staticItems: jobMenuItems.getStaticItems() }}
      id="job-sewer-map"
    >
      {currentJobStorage.currentJob.sewerObjectsCount === 0 ? (
        <div className="message-info">
          <Svg className="svg-info" svg={Info} />
          <span>
            {translationService.translate("job-sewer-objects.no-map-data-part1")}
            <Link to={`sewer-objects`}>
              {translationService.translate("job-sewer-objects.no-map-data-part2")}
            </Link>
            {translationService.translate("job-sewer-objects.no-map-data-part3")}
          </span>
        </div>
      ) : (
        <>
          <div className="map-page-container">
            <div className="map-container">
              <SewerObjectsMap sewerObjectsMapStorage={mapStorage} />
            </div>
            <div className="table-container">
              <DataGrid
                itemTypeKey="general.sewer-objects"
                dataGridStorage={mapStorage.sewerObjectsStorage}
                toolbarCustomButton={{
                  icon: CheckCircleIcon,
                  textKey: "general.data-grid.toolbarFilterMap",
                  callback: filterMapData,
                  isDisabled: mapStorage.isFilterMapDisabled,
                }}
                filterModelChangeCallback={() => mapStorage.setIsFilterMapDisabled(false)}
                hasDefaultColumnVisibility
                singleSelection
                isOnSide
              />
              <SewerObjectsMapLegend />
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
});
